import { a as __rest } from '../../../tslib.es6.esm.js';
import { jsx } from 'react/jsx-runtime';
import React__default, { useRef } from 'react';
import { StickyContainerContext } from '../StickyContainer/StickyContainer.esm.js';
import { STICKY_POSITION, STICKY_BEHAVIOR, TRIGGER_POSITION } from '../constants.esm.js';
import StickyElementStyles from './StickyElement.styles.esm.js';
import { cn } from '../../../utils/cn.esm.js';

// Base component without context
const StickyElementBase = React__default.forwardRef((props, ref) => {
    const internalRef = useRef(null);
    // Use useImperativeHandle to expose the ref
    React__default.useImperativeHandle(ref, () => internalRef.current, []);
    const { children, position = STICKY_POSITION.TOP, className, instance = "one", isScrollingUp, hasPassedTrigger, triggerPositions, behavior, "aria-live": ariaLive } = props, rest = __rest(props, ["children", "position", "className", "instance", "isScrollingUp", "hasPassedTrigger", "triggerPositions", "behavior", "aria-live"]);
    const showSticky = (() => {
        if (behavior === STICKY_BEHAVIOR.ON_SCROLL) {
            const currentTriggerPosition = triggerPositions && triggerPositions[instance]; // Get current trigger position from context
            // Adjusted to "above" based on typical behavior. If position is bottom, below is above of the trigger
            const isAtTriggerPoint = currentTriggerPosition === TRIGGER_POSITION.ABOVE;
            return isScrollingUp && isAtTriggerPoint;
        }
        else if (behavior === STICKY_BEHAVIOR.PERSISTENT) {
            // Persistent behavior: Show when past trigger, hide when scrolling back above trigger
            // Depends on hasPassedTrigger (indicates whether past trigger or not)
            return hasPassedTrigger && hasPassedTrigger[instance];
        }
        return false; // Default to not showing if behavior is undefined
    })();
    // Don't render if not showing
    if (!showSticky) {
        return null;
    }
    // Determine if element should be visible
    const isVisible = showSticky;
    const { container } = StickyElementStyles({
        position,
        visible: isVisible,
    });
    return (jsx("div", Object.assign({ "data-cmp": "stickyElement", ref: internalRef, className: cn(container(), className), "aria-live": ariaLive }, rest, { children: children })));
});
// Component with context - this will be the main StickyElement
const StickyElement = React__default.forwardRef((props, ref) => (jsx(StickyContainerContext.Consumer, { children: (context) => context && (jsx(StickyElementBase, Object.assign({}, props, { ref: ref, isScrollingUp: context.isScrollingUp, hasPassedTrigger: context.hasPassedTrigger, triggerPositions: context.triggerPositions }))) })));
// Set the display name for better debugging
StickyElement.displayName = "StickyElement";

export { StickyElement };
